
import { useState } from "react";
import { Trash2, Loader2, BookmarkX } from "lucide-react";
import { Idea } from "./IdeaCard";
import IdeaCard from "./IdeaCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { historyService } from "@/lib/historyUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SavedIdeasSectionProps {
  savedIdeas: Idea[];
  setSavedIdeas: React.Dispatch<React.SetStateAction<Idea[]>>;
}

export default function SavedIdeasSection({ savedIdeas, setSavedIdeas }: SavedIdeasSectionProps) {
  const { user } = useAuth();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemoveIdea = async (ideaId: string) => {
    try {
      if (user) {
        // Remove from database for authenticated users
        console.log("🗑️ Removing idea from database");
        await historyService.removeSavedIdea(user.id, ideaId);
        
        // Update local state
        const updatedIdeas = savedIdeas.filter(idea => idea.id !== ideaId);
        setSavedIdeas(updatedIdeas);
        
        toast.success("Idea removed from saved collection");
      } else {
        // Fallback to localStorage for non-authenticated users
        console.log("🗑️ Removing idea from localStorage");
        const updatedIdeas = savedIdeas.filter(idea => idea.id !== ideaId);
        setSavedIdeas(updatedIdeas);
        localStorage.setItem("savedIdeas", JSON.stringify(updatedIdeas));
        toast.success("Idea removed from saved collection");
      }
    } catch (error) {
      console.error("Error removing idea:", error);
      
      // Fallback to localStorage even for authenticated users
      if (user) {
        console.log("🔄 Database removal failed, falling back to localStorage");
        try {
          const updatedIdeas = savedIdeas.filter(idea => idea.id !== ideaId);
          setSavedIdeas(updatedIdeas);
          localStorage.setItem("savedIdeas", JSON.stringify(updatedIdeas));
          toast.success("Idea removed (offline)");
        } catch (fallbackError) {
          toast.error("Failed to remove idea");
        }
      } else {
        toast.error("Failed to remove idea");
      }
    }
  };

  const handleClearAllIdeas = async () => {
    try {
      setIsRemoving(true);
      
      if (user) {
        // Clear from database for authenticated users
        console.log("🗑️ Clearing all ideas from database");
        await historyService.clearAllSavedIdeas(user.id);
        
        // Update local state
        setSavedIdeas([]);
        
        toast.success("All saved ideas cleared");
      } else {
        // Fallback to localStorage for non-authenticated users
        console.log("🗑️ Clearing all ideas from localStorage");
        setSavedIdeas([]);
        localStorage.removeItem("savedIdeas");
        toast.success("All saved ideas cleared");
      }
    } catch (error) {
      console.error("Error clearing ideas:", error);
      
      // Fallback to localStorage even for authenticated users
      if (user) {
        console.log("🔄 Database clear failed, falling back to localStorage");
        try {
          setSavedIdeas([]);
          localStorage.removeItem("savedIdeas");
          toast.success("All ideas cleared (offline)");
        } catch (fallbackError) {
          toast.error("Failed to clear ideas");
        }
      } else {
        toast.error("Failed to clear ideas");
      }
    } finally {
      setIsRemoving(false);
    }
  };

  if (savedIdeas.length === 0) {
    return (
      <div className="text-center py-16 glass-card rounded-2xl animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <BookmarkX className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-lg font-medium mb-2">No saved ideas yet</h3>
        <p className="text-muted-foreground text-sm max-w-sm mx-auto">
          Save ideas you like from your capture results and they'll appear here for easy access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Your Saved Ideas</h2>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-destructive hover:text-destructive"
              disabled={isRemoving || savedIdeas.length === 0}
            >
              {isRemoving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Clear All
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear all saved ideas?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete all your saved creative ideas.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleClearAllIdeas}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      <div className="space-y-6">
        {savedIdeas.map((idea) => (
          <div key={idea.id} className="relative">
            <IdeaCard 
              idea={idea}
              isDetailed={false}
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 h-8 w-8 bg-background/80 hover:bg-background text-destructive"
              onClick={() => handleRemoveIdea(idea.id)}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Remove saved idea</span>
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
